import api from "@/lib/api";

export type GameReceivedRatingsSummary = {
  ratingsCount: number;
  averageStars: number | null;
  effectiveSkill: number;
};

export type RateableTeammate = {
  userId: string;
  name: string;
  avatar?: string | null;
  myStars: number | null;
};

export type RateableTeammatesPayload = {
  teammates: RateableTeammate[];
  canSubmitRatings: boolean;
};

export type UserRatingScore = {
  userId: string;
  ratingsCount: number;
  averageStars: number;
  score: number;
};

export type AdminRatingEntry = {
  raterUserId: string;
  raterName: string;
  raterAvatar: string | null;
  ratedUserId: string;
  ratedName: string;
  ratedAvatar: string | null;
  stars: number;
  updatedAt: string;
};

export type AdminRatingReceivedSummary = {
  userId: string;
  name: string;
  avatar: string | null;
  averageStars: number;
  ratingsReceived: number;
};

export type AdminRatingsOverview = {
  entries: AdminRatingEntry[];
  receivedSummary: AdminRatingReceivedSummary[];
};

export type MyReceivedRatingEntry = {
  id: string;
  stars: number;
  createdAt: string;
  rater: {
    userId: string;
    name: string;
    avatar: string | null;
  };
  game: {
    id: string;
    name: string;
    date: string;
    teamId: string | null;
    teamName: string | null;
  };
};

export type MyReceivedRatingsPayload = {
  summary: { ratingsCount: number; averageStars: number | null };
  entries: MyReceivedRatingEntry[];
};

export class GameRatingService {
  async getTeammates(gameId: string): Promise<RateableTeammatesPayload> {
    const response = await api.get<RateableTeammatesPayload>(`/games/${gameId}/ratings/teammates`);
    return response.data;
  }

  async submitRating(gameId: string, ratedUserId: string, stars: number) {
    const response = await api.post(`/games/${gameId}/ratings`, { ratedUserId, stars });
    return response.data;
  }

  async getMyReceivedRatings(): Promise<MyReceivedRatingsPayload> {
    const response = await api.get<MyReceivedRatingsPayload>(`/games/ratings/received/me`);
    return response.data;
  }

  async getUserRatingScore(userId: string): Promise<UserRatingScore> {
    const response = await api.get<UserRatingScore>(`/games/users/${userId}/rating-score`);
    return response.data;
  }

  async getAdminOverview(gameId: string): Promise<AdminRatingsOverview> {
    const response = await api.get<AdminRatingsOverview>(
      `/games/${gameId}/ratings/admin-overview`,
    );
    return response.data;
  }

  async getProgress(gameId: string): Promise<{
    gameId: string;
    totalToRate: number;
    ratedCount: number;
    pendingCount: number;
    completed: boolean;
    organizerView?: boolean;
    listedCount?: number;
    myReceivedInGame: GameReceivedRatingsSummary;
  }> {
    const response = await api.get(`/games/${gameId}/ratings/progress`);
    return response.data;
  }
}

export const gameRatingService = new GameRatingService();
